<template>
  <div class="plugin-panel" v-loading="loading">
    <el-card class="workspace-card toolbar-card" shadow="hover">
      <div class="page-toolbar">
        <div>
          <div class="toolbar-title">MazdaServer.dll 安全热更新</div>
          <div class="toolbar-desc">
            不覆盖已加载 DLL。上传先在{{ targetLabel }}生成不可变版本、执行 ABI/自检并进入候选灰度，确认后再正式提升。
          </div>
        </div>
        <div class="toolbar-actions">
          <div class="local-only-switch" title="打开后：上传、提升、撤销、回滚、重载只处理本端服务器，不同步另一台服务器">
            <el-switch v-model="localOnly" :disabled="operationBusy || hasCandidate" active-text="不同步对端" />
            <small v-if="hasCandidate">候选期间固定使用上传时的同步范围</small>
          </div>
          <el-button :loading="loading" :disabled="operationBusy" @click="loadInfo">刷新</el-button>
          <el-button
            :loading="reloading"
            :disabled="hasCandidate || operationBusy"
            @click="reloadDll"
          >
            重新加载活动版本
          </el-button>
        </div>
      </div>
    </el-card>

    <el-alert
      v-if="localOnly"
      title="已开启“不同步对端”"
      description="上传、提升、撤销、回滚和重新加载都只处理本端服务器，不会再同步另一台服务器。调试完成后请记得关闭此开关。"
      type="warning"
      show-icon
      :closable="false"
    />

    <el-alert
      v-if="operationNotice"
      :title="operationNotice.title"
      :description="operationNotice.message"
      :type="operationNotice.type"
      show-icon
      @close="clearOperationNotice"
    />

    <div class="summary-grid">
      <div class="summary-item">
        <span class="summary-item-label">生命周期</span>
        <strong>{{ lifecycleText }}</strong>
      </div>
      <div class="summary-item">
        <span class="summary-item-label">ABI</span>
        <strong>{{ info.abiVersion || '-' }}</strong>
      </div>
      <div class="summary-item">
        <span class="summary-item-label">灰度比例</span>
        <strong>{{ info.canaryPercent || 0 }}%</strong>
      </div>
      <div class="summary-item">
        <span class="summary-item-label">保留 Zombie</span>
        <strong>{{ info.zombieCount || 0 }}</strong>
      </div>
      <div class="summary-item wide">
        <span class="summary-item-label">活动制品</span>
        <strong>{{ info.activeArtifact || '-' }}</strong>
        <small v-if="info.activeSha256">SHA-256：{{ info.activeSha256 }}</small>
      </div>
      <div class="summary-item wide">
        <span class="summary-item-label">活动版本</span>
        <strong>{{ info.version || '-' }}</strong>
        <small>{{ info.loadedPath || '-' }}</small>
      </div>
    </div>

    <el-alert
      v-if="info.lastError"
      :title="info.lastError"
      type="warning"
      show-icon
      :closable="false"
    />

    <el-card v-if="hasCandidate" class="workspace-card candidate-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>候选灰度</span>
          <el-tag type="warning">尚未正式接管全部业务</el-tag>
        </div>
      </template>

      <div class="candidate-info">
        <div><span class="candidate-label">制品</span><strong>{{ info.candidateArtifact }}</strong></div>
        <div><span class="candidate-label">事务</span><strong>{{ info.candidateTransaction }}</strong></div>
        <div><span class="candidate-label">SHA-256</span><strong>{{ info.candidateSha256 }}</strong></div>
      </div>
      <div class="metric-grid">
        <div><span class="metric-label">灰度调用</span><strong>{{ info.candidateCalls }}</strong></div>
        <div><span class="metric-label">失败</span><strong>{{ info.candidateFailures }}</strong></div>
        <div><span class="metric-label">超时</span><strong>{{ info.candidateTimeouts }}</strong></div>
      </div>
      <el-alert
        title="候选发生 2 次超时，或在至少 5 次调用后失败率达到阈值时，后端会自动撤销候选并继续使用旧活动版本。"
        type="info"
        :closable="false"
        show-icon
      />
      <div class="candidate-actions">
        <el-button
          type="success"
          :loading="promoting"
          :disabled="operationBusy"
          @click="promoteDll"
        >
          正式提升到{{ targetLabel }}
        </el-button>
        <el-button
          type="danger"
          plain
          :loading="aborting"
          :disabled="operationBusy"
          @click="abortCandidate"
        >
          撤销{{ targetLabel }}候选
        </el-button>
      </div>
    </el-card>

    <el-card class="workspace-card" shadow="hover">
      <template #header>
        <div class="card-header"><span>上传候选 DLL</span></div>
      </template>
      <div class="tip-line">Windows x64 DLL，最大 180 MB；上传仅进入候选灰度，需再点“正式提升”才会切换活动版本。</div>
      <div class="upload-row">
        <el-upload
          v-model:file-list="uploadFileList"
          :auto-upload="false"
          :show-file-list="true"
          :limit="1"
          accept=".dll"
          :disabled="hasCandidate || operationBusy"
          :on-change="(file) => chooseFile(file.raw)"
          :on-remove="() => chooseFile(null)"
        >
          <el-button :disabled="hasCandidate || operationBusy">选择 DLL</el-button>
        </el-upload>
        <el-button
          type="primary"
          :disabled="hasCandidate || operationBusy"
          :loading="uploading"
          @click="uploadDll"
        >
          暂存并启动{{ targetLabel }}灰度
        </el-button>
      </div>
    </el-card>

    <el-card class="workspace-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>不可变版本 / {{ targetLabel }}回滚</span>
          <small class="history-hint">
            通常保留 {{ historyLimit }} 个；在用版本受保护，除最新两个历史版本外可手动删除
          </small>
        </div>
      </template>
      <el-table :data="historyRows" border stripe empty-text="暂无版本制品">
        <el-table-column prop="name" label="制品文件" min-width="300" show-overflow-tooltip />
        <el-table-column label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.active" type="success">活动</el-tag>
            <el-tag v-else-if="row.candidate" type="warning">候选</el-tag>
            <el-tag v-else type="info" effect="plain">历史</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template #default="{ row }">
            <div class="row-actions">
              <el-button
                type="warning"
                link
                :disabled="row.active || hasCandidate || operationBusy"
                :loading="rollingBack"
                @click="rollbackDll(row.name)"
              >
                {{ targetLabel }}回滚
              </el-button>
              <el-button
                v-if="row.deletable"
                type="danger"
                link
                :disabled="hasCandidate || operationBusy"
                :loading="deleting"
                @click="deleteHistory(row)"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useMazdaPlugin } from '@/composables/mazda/useMazdaPlugin';

const props = defineProps({
  serverName: { type: String, default: '' },
  mazdaPost: { type: Function, required: true },
});

const {
  loading,
  uploading,
  promoting,
  aborting,
  reloading,
  rollingBack,
  deleting,
  operationBusy,
  operationNotice,
  selectedFile,
  info,
  historyRows,
  historyLimit,
  hasCandidate,
  localOnly,
  loadInfo,
  chooseFile,
  uploadDll,
  promoteDll,
  abortCandidate,
  reloadDll,
  rollbackDll,
  deleteHistory,
  clearOperationNotice,
} = useMazdaPlugin(props.mazdaPost, () => props.serverName);

const uploadFileList = ref([]);
const targetLabel = computed(() => localOnly.value
  ? '本机（不同步对端）'
  : (props.serverName === 'Local Server' ? '本机' : 'CN/US 两端'));

const lifecycleText = computed(() => {
  if (info.value.lifecycleState === 'canary') return '候选灰度中';
  if (info.value.lifecycleState === 'active') return '活动版本';
  return '未加载';
});

watch(() => props.serverName, loadInfo);
watch(selectedFile, (file) => {
  if (!file) uploadFileList.value = [];
});
</script>

<style scoped>

.plugin-panel { display: flex; flex-direction: column; gap: 14px; }
.page-toolbar { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; }
.toolbar-title { font-size: 17px; font-weight: 700; color: var(--wf-text-primary, #1f2d3d); }
.toolbar-desc { margin-top: 5px; color: var(--wf-text-muted, #667085); font-size: 13px; line-height: 1.6; }
.toolbar-actions, .candidate-actions, .upload-row { display: flex; gap: 12px; flex-wrap: wrap; }
.local-only-switch { display: flex; align-items: center; gap: 10px; margin-right: 4px; color: var(--wf-text-regular, #4b5565); }
.local-only-switch small { color: var(--wf-text-muted, #98a2b3); }

.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(130px, 1fr)); gap: 10px; }
.summary-item {
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--wf-surface-soft, #f8fafc);
  border: 1px solid var(--wf-border, #eef2f6);
}
.summary-item.wide { grid-column: span 2; }
.summary-item-label, .candidate-label, .metric-label {
  display: block;
  color: var(--wf-text-muted, #667085);
  font-size: 12px;
  font-weight: 400;
}
.summary-item strong, .candidate-info strong, .metric-grid strong {
  display: block;
  margin-top: 5px;
  color: var(--wf-text-primary, #1f2d3d);
  font-weight: 600;
  word-break: break-all;
}
.summary-item small {
  display: block;
  margin-top: 5px;
  color: var(--wf-text-muted, #98a2b3);
  font-size: 12px;
  word-break: break-all;
}

.card-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; font-weight: 700; color: var(--wf-text-primary, #1f2d3d); }
.card-header .history-hint { font-size: 12px; font-weight: 400; color: var(--wf-text-muted, #98a2b3); }

.candidate-card { border-color: rgba(230, 162, 60, 0.45); }
.candidate-info {
  display: grid;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--wf-status-warning-bg, #fffbeb);
  border: 1px solid rgba(230, 162, 60, 0.25);
  color: var(--wf-text-regular, #4b5565);
}
.metric-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 12px 0; }
.metric-grid > div {
  padding: 12px;
  border-radius: 10px;
  background: var(--wf-surface-soft, #f8fafc);
  border: 1px solid var(--wf-border, #eef2f6);
  text-align: center;
}
.candidate-actions { margin-top: 14px; }

.tip-line { color: var(--wf-text-muted, #667085); font-size: 13px; line-height: 1.8; }
.upload-row { align-items: flex-start; margin-top: 14px; }
.row-actions { display: inline-flex; align-items: center; gap: 14px; }

.row-actions :deep(.el-button),
.row-actions .el-button {
  height: auto !important;
  padding: 2px 4px !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  border-radius: 6px !important;
}
.row-actions :deep(.el-button:hover),
.row-actions .el-button:hover {
  transform: none !important;
  box-shadow: none !important;
}
.row-actions :deep(.el-button--warning.is-link),
.row-actions .el-button--warning.is-link {
  color: #b45309 !important;
  background: transparent !important;
  border-color: transparent !important;
}
.row-actions :deep(.el-button--warning.is-link:hover),
.row-actions .el-button--warning.is-link:hover {
  color: #d97706 !important;
  background: rgba(230, 162, 60, 0.1) !important;
}
.row-actions :deep(.el-button--danger.is-link),
.row-actions .el-button--danger.is-link {
  color: #dc2626 !important;
  background: transparent !important;
  border-color: transparent !important;
}
.row-actions :deep(.el-button--danger.is-link:hover),
.row-actions .el-button--danger.is-link:hover {
  color: #ef4444 !important;
  background: rgba(245, 108, 108, 0.1) !important;
}
.row-actions :deep(.el-button.is-disabled),
.row-actions .el-button.is-disabled,
.row-actions :deep(.el-button.is-disabled:hover),
.row-actions .el-button.is-disabled:hover {
  color: var(--wf-text-placeholder, #c0c4cc) !important;
  background: transparent !important;
  border-color: transparent !important;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(130px, 1fr)); }
  .summary-item.wide { grid-column: 1 / -1; }
  .page-toolbar { flex-direction: column; }
}
</style>

<style>

.el-overlay:has(.dll-confirm-box) {
  background-color: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(2px);
}
html.dark .el-overlay:has(.dll-confirm-box) {
  background-color: rgba(2, 6, 23, 0.66);
}

.el-message-box.dll-confirm-box {
  border-radius: 16px;
  padding: 22px 24px 20px;
  border: 1px solid rgba(226, 233, 243, 0.9);
  box-shadow:
    0 24px 64px rgba(15, 23, 42, 0.18),
    0 2px 8px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}
html.dark .el-message-box.dll-confirm-box {
  background: var(--wf-surface-elevated, #1b2638);
  border: 1px solid var(--wf-border, rgba(148, 163, 184, 0.24));
  box-shadow: 0 28px 72px rgba(0, 0, 0, 0.55);
}

.el-message-box.dll-confirm-box .el-message-box__header {
  padding-bottom: 12px;
}
.el-message-box.dll-confirm-box .el-message-box__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--wf-text-primary, #1f2d3d);
  letter-spacing: 0.2px;
}
.el-message-box.dll-confirm-box .el-message-box__headerbtn {
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}
.el-message-box.dll-confirm-box .el-message-box__headerbtn:hover {
  background: rgba(15, 23, 42, 0.06);
}
html.dark .el-message-box.dll-confirm-box .el-message-box__headerbtn:hover {
  background: rgba(255, 255, 255, 0.08);
}
.el-message-box.dll-confirm-box .el-message-box__content {
  padding-top: 4px;
}
.el-message-box.dll-confirm-box .el-message-box__btns {
  padding-top: 20px;
  gap: 10px;
}
.el-message-box.dll-confirm-box .el-message-box__btns .el-button {
  min-width: 92px;
  height: 36px;
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  margin-left: 0;
}
.el-message-box.dll-confirm-box .el-message-box__btns .el-button--primary {
  border: none;
  background: linear-gradient(135deg, #4f9cff 0%, #3a8cff 100%);
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.22);
  transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.el-message-box.dll-confirm-box .el-message-box__btns .el-button--primary:hover {
  background: linear-gradient(135deg, #66a8ff 0%, #4a94ff 100%);
  box-shadow: 0 10px 22px rgba(64, 158, 255, 0.3);
}
.el-message-box.dll-confirm-box .el-message-box__btns .el-button--primary:active {
  transform: translateY(0);
}

.el-message-box.dll-confirm-box .dll-confirm-danger-btn,
.el-message-box.dll-confirm-box .dll-confirm-danger-btn.el-button--danger {
  border: none;
  background: linear-gradient(135deg, #f56c6c 0%, #e75252 100%);
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(245, 108, 108, 0.22);
  transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.el-message-box.dll-confirm-box .dll-confirm-danger-btn:hover,
.el-message-box.dll-confirm-box .dll-confirm-danger-btn:focus {
  background: linear-gradient(135deg, #ff7f7f 0%, #f06464 100%);
  box-shadow: 0 10px 22px rgba(245, 108, 108, 0.3);
  color: #ffffff;
}
.el-message-box.dll-confirm-box .dll-confirm-danger-btn:active {
  transform: translateY(0);
}

.dll-confirm-body {
  line-height: 1.75;
  font-size: 14px;
  color: var(--wf-text-primary, #303133);
  word-break: break-word;
}
.dll-confirm-body .dll-confirm-text {
  margin: 0 0 10px;
  color: var(--wf-text-regular, #475569);
}
.dll-confirm-body .dll-confirm-text:last-child {
  margin-bottom: 0;
}
.dll-confirm-body .dll-confirm-sub {
  margin-top: 10px;
  color: var(--wf-text-muted, #909399);
  font-size: 12.5px;
}
.dll-confirm-body .dll-confirm-warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--wf-status-warning-text, #b45209);
  background: var(--wf-status-warning-bg, #fffbeb);
  border: 1px solid rgba(230, 162, 60, 0.28);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12.5px;
  line-height: 1.6;
}
.dll-confirm-body .dll-confirm-warn::before {
  content: '';
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border-radius: 50%;
  background:
    radial-gradient(circle at center, var(--wf-status-warning-text, #b45209) 0 2px, transparent 2.5px),
    var(--wf-status-warning-bg, #fffbeb);
  border: 1.5px solid var(--wf-status-warning-text, #b45209);
  box-sizing: border-box;
}

.dll-confirm-artifact {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 4px;
  padding: 10px 12px;
  background: var(--wf-surface-soft, #f8fafc);
  border: 1px solid var(--wf-border, #e4e7ed);
  border-radius: 10px;
}
html.dark .dll-confirm-artifact {
  background: rgba(255, 255, 255, 0.04);
  border-color: var(--wf-border, rgba(148, 163, 184, 0.24));
}
.dll-confirm-artifact::before {
  content: '';
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(64, 158, 255, 0.14), rgba(64, 158, 255, 0.05)),
    var(--wf-surface, #ffffff);
  border: 1px solid rgba(64, 158, 255, 0.22);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9cff' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/%3E%3Cpolyline points='14 2 14 8 20 8'/%3E%3Cline x1='9' y1='13' x2='15' y2='13'/%3E%3Cline x1='9' y1='17' x2='13' y2='17'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px 16px;
}
html.dark .dll-confirm-artifact::before {
  background-color: rgba(64, 158, 255, 0.1);
}
.dll-confirm-artifact-text {
  flex: 1;
  min-width: 0;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.65;
  color: var(--wf-text-regular, #475569);
  word-break: break-all;
  overflow-wrap: anywhere;
  white-space: normal;
  user-select: text;
}
html.dark .dll-confirm-artifact-text {
  color: #cbd5e1;
}
.dll-confirm-copy {
  flex: 0 0 auto;
  margin-top: 0;
}
.dll-confirm-copy.el-button {
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
}
</style>
